import {
  formatAppearanceLocation,
  formatAppearanceSubject,
} from "./outlookAppearanceFormat.js";

const CLS_START = "--- CLS CONNECT START ---";
const CLS_END = "--- CLS CONNECT END ---";
const CLS_PROPERTY = "clsAppearance";
const clean = (value) => String(value || "").trim();

function localDateTimeParts(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return { date: "", time: "" };
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return { date: [year, month, day].join("-"), time: [hour, minute].join(":") };
}

function readDescriptionField(description, label) {
  const escaped = label.replace(/[.*+?^$(){}|[\]\\]/g, "\\$&");
  const match = String(description || "").match(
    new RegExp("(?:^|\\n)" + escaped + ":\\s*([^\\n]*)", "i")
  );
  return clean(match && match[1]);
}

function splitLocation(value) {
  const parts = clean(value).split(/\s+-\s+/).filter(Boolean);
  return { court: parts[0] || "", listingLocation: parts.slice(1).join(" - ") };
}

export function readGoogleCalendarAppearance(event) {
  let stored = {};
  const rawStored = event && event.extendedProperties &&
    event.extendedProperties.private &&
    event.extendedProperties.private[CLS_PROPERTY];
  if (rawStored) {
    try {
      stored = JSON.parse(rawStored);
    } catch {
      stored = {};
    }
  }

  const start = localDateTimeParts(event && event.start && (event.start.dateTime || event.start.date));
  const end = localDateTimeParts(event && event.end && (event.end.dateTime || event.end.date));
  const location = splitLocation(event && event.location);
  const subjectParts = clean(event && event.summary).split(/\s+-\s+/);
  const description = event && event.description || "";

  return {
    ...stored,
    clientName: stored.clientName || readDescriptionField(description, "Client") || subjectParts[0] || "",
    fileNumber: stored.fileNumber || readDescriptionField(description, "Matter"),
    appearanceType: stored.appearanceType || readDescriptionField(description, "Appearance") || subjectParts.slice(1).join(" - ") || "Mention",
    date: stored.date || start.date,
    startTime: stored.startTime || start.time || "09:00",
    endTime: stored.endTime || end.time || "10:00",
    court: stored.court || readDescriptionField(description, "Court") || location.court,
    listingLocation: stored.listingLocation || readDescriptionField(description, "Listed at") || location.listingLocation,
    fundingType: stored.fundingType || readDescriptionField(description, "Funding") || "LACW",
    grantOfAidNumber: stored.grantOfAidNumber || readDescriptionField(description, "Grant of aid"),
    nextCourtDate: stored.nextCourtDate || readDescriptionField(description, "Next court date"),
    nextCourtTime: stored.nextCourtTime || readDescriptionField(description, "Next court time") || "09:00",
    nextAppearanceType: stored.nextAppearanceType || readDescriptionField(description, "Next appearance"),
    outcome: stored.outcome || readDescriptionField(description, "Outcome"),
    notes: stored.notes || readDescriptionField(description, "Notes"),
  };
}

function appearanceDescription(appearance) {
  return [
    CLS_START,
    "Client: " + clean(appearance.clientName),
    "Matter: " + clean(appearance.fileNumber),
    "Appearance: " + clean(appearance.appearanceType),
    "Court: " + clean(appearance.court),
    "Listed at: " + clean(appearance.listingLocation),
    "Funding: " + clean(appearance.fundingType),
    "Grant of aid: " + clean(appearance.grantOfAidNumber),
    "Next court date: " + clean(appearance.nextCourtDate),
    "Next court time: " + clean(appearance.nextCourtTime),
    "Next appearance: " + clean(appearance.nextAppearanceType),
    "Outcome: " + clean(appearance.outcome),
    "Notes: " + clean(appearance.notes),
    CLS_END,
  ].join("\n");
}

export function mergeGoogleCalendarDescription(existingDescription, appearance) {
  const current = String(existingDescription || "");
  const block = appearanceDescription(appearance);
  const start = current.indexOf(CLS_START);
  const end = current.indexOf(CLS_END);
  if (start >= 0 && end >= start) {
    return (current.slice(0, start) + block + current.slice(end + CLS_END.length)).trim();
  }
  return (block + (current ? "\n\n" + current : "")).trim();
}

export function buildGoogleCalendarEvent(appearance, existingEvent = {}) {
  const storedAppearance = {
    actionstepMatterId: clean(appearance.actionstepMatterId),
    clientName: clean(appearance.clientName),
    fileNumber: clean(appearance.fileNumber),
    appearanceType: clean(appearance.appearanceType),
    date: clean(appearance.date),
    startTime: clean(appearance.startTime),
    endTime: clean(appearance.endTime),
    court: clean(appearance.court),
    listingLocation: clean(appearance.listingLocation),
    fundingType: clean(appearance.fundingType),
    grantOfAidNumber: clean(appearance.grantOfAidNumber),
    nextCourtDate: clean(appearance.nextCourtDate),
    nextCourtTime: clean(appearance.nextCourtTime),
    nextAppearanceType: clean(appearance.nextAppearanceType),
    outcome: clean(appearance.outcome),
    notes: clean(appearance.notes),
  };
  const existingProperties = existingEvent.extendedProperties || {};
  const existingPrivate = existingProperties.private || {};

  return {
    summary: formatAppearanceSubject(appearance),
    location: formatAppearanceLocation(appearance),
    description: mergeGoogleCalendarDescription(existingEvent.description, appearance),
    start: {
      dateTime: appearance.date + "T" + (appearance.startTime || "09:00") + ":00",
      timeZone: "Australia/Sydney",
    },
    end: {
      dateTime: appearance.date + "T" + (appearance.endTime || "10:00") + ":00",
      timeZone: "Australia/Sydney",
    },
    extendedProperties: {
      ...existingProperties,
      private: {
        ...existingPrivate,
        [CLS_PROPERTY]: JSON.stringify(storedAppearance),
      },
    },
  };
}

export function formatGoogleEventOption(event) {
  const start = localDateTimeParts(event && event.start && (event.start.dateTime || event.start.date));
  return [start.date, start.time, clean(event && event.summary) || "Untitled event"]
    .filter(Boolean)
    .join(" - ");
}
